import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from '../utils/api';

const AdminNotificationManager = () => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [targetAllUsers, setTargetAllUsers] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [actionLabel, setActionLabel] = useState('Open');
  const [actionUrl, setActionUrl] = useState('');

  const loadUsers = async (searchTerm = '') => {
    try {
      setLoadingUsers(true);
      setError('');
      const params = searchTerm.trim() ? { search: searchTerm.trim() } : {};
      const response = await axios.get('/api/notifications/admin/recipients', { params });
      setUsers(Array.isArray(response.data?.users) ? response.data.users : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const visibleUsers = useMemo(() => {
    if (!search.trim()) {
      return users;
    }

    const query = search.trim().toLowerCase();
    return users.filter((user) => {
      return (
        (user.name || '').toLowerCase().includes(query)
        || (user.email || '').toLowerCase().includes(query)
      );
    });
  }, [search, users]);

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((current) => {
      if (current.includes(userId)) {
        return current.filter((id) => id !== userId);
      }
      return [...current, userId];
    });
  };

  const submitNotification = async (event) => {
    event.preventDefault();
    try {
      setCreating(true);
      setError('');
      setSuccess('');

      const payload = {
        title: title.trim(),
        message: message.trim(),
        action_label: actionLabel.trim() || null,
        action_url: actionUrl.trim() || null,
        target_all_users: targetAllUsers,
        user_ids: targetAllUsers ? [] : selectedUserIds,
      };

      const response = await axios.post('/api/notifications/admin', payload);
      setSuccess(`Notification created for ${response.data?.created_count || 0} user(s).`);
      setTitle('');
      setMessage('');
      setActionLabel('Open');
      setActionUrl('');
      if (!targetAllUsers) {
        setSelectedUserIds([]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create notification');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={11}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <h2 className="h4 mb-3">Admin Notification Manager</h2>
              <p className="text-muted mb-3">
                Create notifications for all users or selected users. Users can mark them as read or delete them.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

              <Form onSubmit={submitNotification}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        type="text"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        required
                        maxLength={150}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Action Label (Optional)</Form.Label>
                      <Form.Control
                        type="text"
                        value={actionLabel}
                        onChange={(event) => setActionLabel(event.target.value)}
                        maxLength={80}
                        placeholder="Open"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    required
                    maxLength={2000}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Action URL (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={actionUrl}
                    onChange={(event) => setActionUrl(event.target.value)}
                    maxLength={255}
                    placeholder="/buyer/cart or https://example.com"
                  />
                </Form.Group>

                <Form.Check
                  type="switch"
                  id="target-all-users"
                  checked={targetAllUsers}
                  onChange={(event) => setTargetAllUsers(event.target.checked)}
                  label="Send to all users"
                  className="mb-3"
                />

                {!targetAllUsers && (
                  <Card className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                        <Form.Control
                          type="text"
                          placeholder="Search users by name or email"
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                        />
                        <Button
                          type="button"
                          variant="outline-secondary"
                          onClick={() => loadUsers(search)}
                          disabled={loadingUsers}
                        >
                          Refresh
                        </Button>
                      </div>
                      <small className="text-muted d-block mb-2">Selected users: {selectedUserIds.length}</small>

                      {loadingUsers ? (
                        <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                      ) : visibleUsers.length === 0 ? (
                        <Alert variant="info" className="mb-0">No users found.</Alert>
                      ) : (
                        <Table size="sm" bordered hover responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Select</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Role</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleUsers.map((user) => (
                              <tr key={user.id}>
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    checked={selectedUserIds.includes(user.id)}
                                    onChange={() => toggleUserSelection(user.id)}
                                  />
                                </td>
                                <td>{user.name}</td>
                                <td>{user.email}</td>
                                <td>{user.role}</td>
                                <td>
                                  {user.is_suspended ? <Badge bg="warning" text="dark">Suspended</Badge> : <Badge bg="success">Active</Badge>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                )}

                <Button type="submit" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Notification'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminNotificationManager;
